import React from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Button,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import { motion } from 'motion/react';
import { Link as RouterLink } from 'react-router';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import mercedesRoomImage from 'figma:asset/f370bc94787c14d2317b94673d0634ec38cf7965.webp';
import amgDetailImage from 'figma:asset/09433cfdb6cef19df4a23efcf57e4bae5be903d8.webp';

const gold = '#C0202A';
const silver = '#C0C0C0';

const pillars = [
  {
    number: '01',
    title: 'Preserve Your Investment',
    body: `Your vehicle, your carpet, your tile, your leather furnishings — these represent significant financial investments. Without regular professional care, surface contaminants cause irreversible damage that dramatically reduces the value and lifespan of each asset.

Paint oxidizes and develops swirl marks that diminish resale value. Carpet fibers break down under the abrasion of embedded soil particles. Tile and grout become permanently stained and structurally compromised. Leather cracks and fades as natural oils are depleted. Professional maintenance prevents these outcomes, protecting the full value of everything you own.`,
  },
  {
    number: '02',
    title: 'Health & Indoor Air Quality',
    body: `Carpets act as giant air filters — trapping allergens, bacteria, dust mites, pet dander, pollen, and pathogens deep within their fibers. When these contaminants accumulate beyond the capacity of regular vacuuming, they are released back into the air you breathe.

The same principle applies to upholstery and tile grout — porous surfaces harbor microorganisms that affect indoor air quality and the health of everyone in your home or business. IICRC-certified professional cleaning eliminates these health hazards thoroughly and safely.`,
  },
  {
    number: '03',
    title: 'The Right Method Matters',
    body: `Consumer-grade products and equipment are designed to be safe for unsupervised use — not to achieve professional results. They lack the temperature capability, extraction power, and chemical concentration of professional-grade systems.

Our truck-mounted cleaning system generates water temperatures and vacuum pressure that simply cannot be replicated with portable equipment. Our agents are professional-grade formulations. Our technique is the product of IICRC certification and decades of refinement. The difference in results is not incremental — it is transformative.`,
  },
];

const faqs = [
  {
    question: 'Why is professional carpet cleaning better than renting a machine?',
    answer: 'Rental machines use cold or lukewarm water and have limited suction. Our truck-mounted system heats water to optimal temperatures and generates far greater extraction power, removing more soil and leaving less moisture — which reduces drying time and prevents mold risk.',
  },
  {
    question: 'Will professional cleaning damage my natural stone or tile?',
    answer: 'Absolutely not — when performed by IICRC-certified professionals who understand the specific requirements of each surface type. Improper cleaning can cause damage, which is exactly why certification and training matter. We use surface-appropriate agents and techniques for every material.',
  },
  {
    question: 'How does paint correction work?',
    answer: 'Paint correction is the process of removing surface defects — swirl marks, scratches, oxidation — through machine polishing using compounds and polishes of varying abrasiveness. The result is a smooth, defect-free paint surface with dramatically improved gloss and reflectivity.',
  },
  {
    question: 'Do I need ceramic coating after paint correction?',
    answer: 'Paint correction restores your paint to its best condition. Ceramic coating or polymer paint protection then seals and protects that corrected surface, providing long-lasting resistance to UV, contamination, and light scratches. It is the logical complement to correction work.',
  },
  {
    question: 'How long does leather last without professional care?',
    answer: "Leather's lifespan is dramatically reduced without regular cleaning and conditioning. In Tucson's dry climate, leather loses its natural oils more quickly, leading to drying and cracking. Professional conditioning every 6 months prevents this deterioration entirely.",
  },
  {
    question: 'Are your cleaning products safe for children and pets?',
    answer: 'Yes. We use professional-grade cleaning agents that are safe for residential use when properly applied and dried. We will advise on appropriate re-entry times after service completion for any specific products used.',
  },
];

export function WhyItMatters() {
  return (
    <Box sx={{ background: '#080808', minHeight: '100vh' }}>
      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          pt: { xs: 18, md: 22 },
          pb: { xs: 12, md: 18 },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${mercedesRoomImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.5)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(90deg, rgba(4,4,4,0.77) 0%, rgba(4,4,4,0.45) 100%)',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <Typography variant="overline" sx={{ color: gold, display: 'block', mb: 2 }}>
              The Case for Excellence
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '5rem' },
                color: '#F2EDE4',
                mb: 4,
                maxWidth: 700,
                lineHeight: 1.05,
              }}
            >
              Why Proper Care{' '}
              <Box component="span" sx={{ color: gold, fontStyle: 'italic' }}>Matters.</Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: alpha('#F2EDE4', 0.65), maxWidth: 580, lineHeight: 2 }}
            >
              Surface cleanliness is not merely aesthetic. It is about preservation, health, value, and the standard of living that luxury ownership demands.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Pillars */}
      <Box sx={{ py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          {pillars.map((pillar, i) => (
            <motion.div
              key={pillar.number}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 3, md: 8 },
                  py: { xs: 6, md: 8 },
                  borderBottom: `1px solid ${alpha(gold, 0.1)}`,
                  '&:first-of-type': { borderTop: `1px solid ${alpha(gold, 0.1)}` },
                }}
              >
                <Box sx={{ flexShrink: 0, minWidth: { md: 120 } }}>
                  <Typography
                    sx={{
                      fontFamily: '"Cormorant Garamond", serif',
                      fontSize: '4rem',
                      fontWeight: 300,
                      color: alpha(gold, 0.25),
                      lineHeight: 1,
                    }}
                  >
                    {pillar.number}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ color: '#F2EDE4', mb: 3, fontSize: { xs: '1.4rem', md: '1.9rem' } }}>
                    {pillar.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: alpha('#F2EDE4', 0.6),
                      lineHeight: 2,
                      whiteSpace: 'pre-line',
                      fontSize: '0.95rem',
                    }}
                  >
                    {pillar.body}
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          ))}
        </Container>
      </Box>

      {/* Visual Break */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: 300, md: 500 },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${amgDetailImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center 30%',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(90deg, rgba(4,4,4,1) 0%, rgba(4,4,4,0.75) 50%, rgba(4,4,4,0.6) 100%)',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', alignItems: 'center' }}>
          <Box>
            <Typography
              variant="h3"
              sx={{
                color: '#F2EDE4',
                fontStyle: 'italic',
                fontWeight: 300,
                maxWidth: 500,
                fontSize: { xs: '1.4rem', md: '2.2rem' },
                lineHeight: 1.4,
              }}
            >
              "The desire to achieve the highest quality results — with every vehicle, every surface — is clearly shown in our work."
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}>
              <Box sx={{ width: 32, height: 1, background: gold }} />
              <Typography variant="overline" sx={{ color: gold, fontSize: '0.62rem' }}>
                The Lindsey Enterprises Standard
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* FAQ */}
      <Box sx={{ py: { xs: 12, md: 18 }, background: '#0A0A0A' }}>
        <Container maxWidth="lg">
          <Grid container spacing={8}>
            <Grid size={{ xs: 12, md: 4 }}>
              <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                <Typography variant="overline" sx={{ color: gold, display: 'block', mb: 2 }}>
                  Questions
                </Typography>
                <Typography variant="h3" sx={{ color: '#F2EDE4', mb: 3, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
                  Frequently Asked
                </Typography>
                <Typography variant="body1" sx={{ color: alpha('#F2EDE4', 0.55), lineHeight: 2, mb: 5 }}>
                  Have more questions? We are always happy to discuss your specific needs and provide honest, expert guidance.
                </Typography>
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="outlined"
                  color="primary"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ py: 1.5, px: 4, fontSize: '0.65rem', letterSpacing: '0.2em' }}
                >
                  Contact Us
                </Button>
              </motion.div>
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>
              {faqs.map((faq, i) => (
                <motion.div
                  key={faq.question}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                >
                  <Accordion
                    disableGutters
                    elevation={0}
                    square
                    sx={{
                      background: 'transparent',
                      borderBottom: `1px solid ${alpha(gold, 0.12)}`,
                      '&:first-of-type': { borderTop: `1px solid ${alpha(gold, 0.12)}` },
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: gold }} />}
                      sx={{
                        px: 0,
                        py: 2,
                        '&:hover': { background: alpha(gold, 0.02) },
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: '"Cormorant Garamond", serif',
                          fontSize: '1.1rem',
                          fontWeight: 500,
                          color: '#F2EDE4',
                        }}
                      >
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, pb: 3 }}>
                      <Typography variant="body2" sx={{ color: alpha('#F2EDE4', 0.6), lineHeight: 1.9 }}>
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </motion.div>
              ))}
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: { xs: 10, md: 14 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h2" sx={{ color: '#F2EDE4', mb: 3, fontSize: { xs: '2rem', md: '3rem' } }}>
            Invest in the Best.{' '}
            <Box component="span" sx={{ color: gold, fontStyle: 'italic' }}>Experience the Difference.</Box>
          </Typography>
          <Typography variant="body1" sx={{ color: alpha('#F2EDE4', 0.6), mb: 6, lineHeight: 2 }}>
            Contact Lindsey Enterprises today for a complimentary consultation on the right services for your vehicle and home.
          </Typography>
          <Button
            component={RouterLink}
            to="/contact"
            variant="contained"
            color="primary"
            size="large"
            endIcon={<ArrowForwardIcon />}
            sx={{ py: 2, px: 8, fontSize: '0.7rem', letterSpacing: '0.2em' }}
          >
            Request a Consultation
          </Button>
        </Container>
      </Box>
    </Box>
  );
}
